// Camera preview on the pre-join screen. The stream is stopped when the camera
// is turned off, the device changes or the page unmounts, so the light goes
// off before LiveKit opens its own track.
import { useEffect, useRef, useState } from 'react';
import { COPY } from '../copy.js';
import type { MediaDevicesPort } from '../services.js';

type PreviewState = 'off' | 'starting' | 'live' | 'unsupported' | 'denied';

export function DevicePreview({
  media,
  enabled,
  deviceId,
}: {
  media: MediaDevicesPort | null;
  enabled: boolean;
  deviceId: string | null;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const [state, setState] = useState<PreviewState>('off');

  useEffect(() => {
    if (!enabled) {
      setState('off');
      return;
    }
    if (!media) {
      setState('unsupported');
      return;
    }
    let stream: MediaStream | null = null;
    let cancelled = false;
    setState('starting');
    media
      .getUserMedia({ video: deviceId ? { deviceId: { exact: deviceId } } : true, audio: false })
      .then((s) => {
        if (cancelled) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        stream = s;
        if (video.current) video.current.srcObject = s;
        setState('live');
      })
      .catch(() => {
        if (!cancelled) setState('denied');
      });
    return () => {
      cancelled = true;
      stream?.getTracks().forEach((t) => t.stop());
      if (video.current) video.current.srcObject = null;
    };
  }, [media, enabled, deviceId]);

  return (
    <div className="preview" data-testid="device-preview" data-state={state}>
      <video ref={video} autoPlay playsInline muted hidden={state !== 'live'} />
      {state === 'off' && <p className="muted">{COPY.previewOff}</p>}
      {state === 'starting' && <p className="muted">{COPY.loading}</p>}
      {state === 'unsupported' && <p className="muted">{COPY.previewUnavailable}</p>}
      {state === 'denied' && <p className="warning">{COPY.previewDenied}</p>}
    </div>
  );
}
